import { ProgramNames } from '../../../models/programs/Programs'
import { PROVIDER_TYPES } from '../../../models/providers/Providers'
import { PROGRAM_TAXONOMY, ProgramCustomerPropertyId } from '../../common/constants/customerProgram'
import { TREATMENT_OPTIONS } from '../../common/constants/treatmentOptions'
import { LTSessionTypes } from '../../sessions/constants'
import { ConsentText, ProgramDisplayLabels } from '../messages'
import { ProgramConfig } from '../types'

export const BLENDED_CARE_THERAPY_PROGRAM = {
  programName: ProgramNames.BlendedCareTherapy,
  blendedCareProgramId: 'c8cb4da4-8957-4cff-9d8e-1fa742f06771',
  customerPropertyId: ProgramCustomerPropertyId.considerLyraTherapy,

  displayLabels: {
    default: ProgramDisplayLabels.therapy,
    sessionSummary: 'therapy',
  },

  associatedProviderTypes: [PROVIDER_TYPES.LYRATHERAPIST.lyraType, PROVIDER_TYPES.LYRAFELLOW.lyraType],
  associatedProviderModalities: ['lyraTherapy'],

  canRescheduleAppointment: true,
  selfPayEligible: true,
  supportsEpisodeInsights: true,

  sessionTypeConfig: {
    sessionType: LTSessionTypes.BCT_SESSION,
    rateStrategy: 'fixed',
  },

  directBookingParams: {
    clientele: PROGRAM_TAXONOMY.clientele.individual,
    treatment: PROGRAM_TAXONOMY.treatment.therapy,
    partner: PROGRAM_TAXONOMY.partner.blendedCare,
    offering: PROGRAM_TAXONOMY.offering.default,
  },

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.INDIVIDUAL_THERAPY],
    isFullTreatmentOption: false,
  },

  consents: [
    {
      consentText: ConsentText.therapyConsent,
      consentUrl: 'https://www.lyrahealth.com/lt-agreement',
      showConsentIf: () => true,
    },
  ],

  noteContentMetadataName: {
    INITIAL_NOTE: 'bctInitialNote',
    SESSION_NOTE: 'bctSessionNote',
    GENERAL_NOTE: 'bctGeneralNote',
    REFERRAL_NOTE: 'baseReferralNote',
    OLD_INITIAL_NOTE: 'initialNote',
    OLD_SESSION_NOTE: 'sessionNote',
    OLD_GENERAL_NOTE: 'generalNote',
    OLD_REFERRAL_NOTE: 'referralNote',
  },
} satisfies ProgramConfig

export const TEENS_THERAPY_PROGRAM = {
  programName: ProgramNames.TeensTherapy,
  blendedCareProgramId: 'b42a0c74-76c0-48c4-8a7e-a767418bd753',
  customerPropertyId: ProgramCustomerPropertyId.considerLyraTherapy,
  isTeenProgram: true,

  displayLabels: {
    default: ProgramDisplayLabels.teensTherapy,
    careType: ProgramDisplayLabels.therapy,
    short: 'Therapy',
    clientProfile: 'Teens',
    sessionSummary: 'therapy',
  },

  associatedProviderTypes: [PROVIDER_TYPES.LYRATEENSTHERAPIST.lyraType],
  associatedProviderModalities: ['lyraTherapy'],

  ageGroup: '13-17',

  canRescheduleAppointment: true,
  selfPayEligible: true,

  sessionTypeConfig: {
    sessionType: LTSessionTypes.LCTT_SESSION,
    rateStrategy: 'fixed',
  },

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.INDIVIDUAL_THERAPY],
    isFullTreatmentOption: false,
  },

  consents: [
    {
      consentText: ConsentText.therapyConsent,
      consentUrl: 'https://www.lyrahealth.com/lt-teen-agreement/',
      showConsentIf: () => true,
    },
  ],

  noteContentMetadataName: {
    INITIAL_NOTE: 'lcttInitialNote',
    SESSION_NOTE: 'lcttSessionNote',
    GENERAL_NOTE: 'lcttGeneralNote',
    REFERRAL_NOTE: 'baseReferralNote',
  },
} satisfies ProgramConfig

export const DIALECTICAL_BEHAVIOR_THERAPY_PROGRAM = {
  programName: ProgramNames.DialecticalBehaviorTherapy,
  blendedCareProgramId: '5bd8fc86-836b-4610-84e5-b11f59fa48df',
  customerPropertyId: ProgramCustomerPropertyId.dialecticalBehaviorTherapy,

  displayLabels: {
    default: ProgramDisplayLabels.dialecticalBehaviorTherapy,
    careType: ProgramDisplayLabels.therapy,
    short: 'DBT',
    clientProfile: 'DBT',
    sessionSummary: 'DBT',
  },

  associatedProviderTypes: [PROVIDER_TYPES.LYRATHERAPIST.lyraType],
  associatedProviderModalities: ['lyraTherapy'],

  canRescheduleAppointment: true,
  selfPayEligible: true,
  supportsEpisodeInsights: true,

  sessionTypeConfig: {
    sessionType: LTSessionTypes.DBT_SESSION,
    rateStrategy: 'fixed',
  },

  directBookingParams: {
    clientele: PROGRAM_TAXONOMY.clientele.individual,
    treatment: PROGRAM_TAXONOMY.treatment.therapy,
    partner: PROGRAM_TAXONOMY.partner.blendedCare,
    offering: PROGRAM_TAXONOMY.offering.dbt,
  },

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.INDIVIDUAL_THERAPY],
    isFullTreatmentOption: false,
  },

  consents: [
    {
      consentText: ConsentText.dialecticalBehaviorTherapyConsent,
      consentUrl: 'https://www.lyrahealth.com/dbt-agreement/',
      showConsentIf: () => true,
    },
  ],

  noteContentMetadataName: {
    INITIAL_NOTE: 'dbtInitialNote',
    SESSION_NOTE: 'dbtSessionNote',
    GENERAL_NOTE: 'dbtGeneralNote',
    REFERRAL_NOTE: 'baseReferralNote',
  },
} satisfies ProgramConfig

export const DIALECTICAL_BEHAVIOR_THERAPY_TEENS_PROGRAM = {
  programName: ProgramNames.DialecticalBehaviorTherapyTeens,
  blendedCareProgramId: 'bc5774bd-071f-4bc5-9137-a5e3d6b4be43',
  customerPropertyId: ProgramCustomerPropertyId.dialecticalBehaviorTherapyTeens,
  isTeenProgram: true,
  supportsGuardianMessaging: true,
  requiresParentLedBooking: true,

  displayLabels: {
    default: ProgramDisplayLabels.dialecticalBehaviorTherapyTeens,
    careType: ProgramDisplayLabels.therapy,
    short: 'DBT Teens',
    clientProfile: 'DBT for Teens',
    sessionSummary: 'DBT',
  },

  associatedProviderTypes: [PROVIDER_TYPES.LYRATHERAPIST.lyraType],
  associatedProviderModalities: ['lyraTherapy'],

  ageGroup: '13-17',

  canRescheduleAppointment: true,
  selfPayEligible: true,

  sessionTypeConfig: {
    sessionType: LTSessionTypes.DBT_TEENS_INTAKE_SESSION,
    followupSessionType: LTSessionTypes.DBT_TEENS_FOLLOWUP_SESSION,
    rateStrategy: 'fixed',
  },

  directBookingParams: {
    clientele: PROGRAM_TAXONOMY.clientele.individual,
    treatment: PROGRAM_TAXONOMY.treatment.therapy,
    partner: PROGRAM_TAXONOMY.partner.blendedCare,
    offering: PROGRAM_TAXONOMY.offering.dbtTeens,
  },

  treatmentOptionProperties: {
    associatedTriageTreatmentOptions: [TREATMENT_OPTIONS.INDIVIDUAL_THERAPY],
    isFullTreatmentOption: false,
  },

  consents: [
    {
      consentText: ConsentText.dialecticalBehaviorTherapyTeensConsent,
      consentUrl: 'https://www.lyrahealth.com/dbt-teen-agreement/',
      showConsentIf: () => true,
    },
  ],

  noteContentMetadataName: {
    INITIAL_NOTE: 'dbttInitialNote',
    SESSION_NOTE: 'dbttCombinedSessionNote',
    GENERAL_NOTE: 'dbtGeneralNote',
    REFERRAL_NOTE: 'baseReferralNote',
  },
} satisfies ProgramConfig
